import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import { 
  BarChart3, 
  FolderOpen, 
  TrendingUp, 
  PlusCircle,
  ArrowRight,
  Calendar
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

export default function Dashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOccupancyColor = (value) => {
    if (value >= 80) return "#34C759";
    if (value >= 50) return "#FF9500";
    return "#FF3B30";
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'BOA': return "#34C759";
      case 'RUIM': return "#FF9500";
      case 'VAZIA': return "#FF3B30";
      case 'CRITICA': return "#8B0000";
      default: return "#6B7280";
    }
  };

  const getClassificationLabel = (classification) => {
    switch (classification) {
      case 'BOA': return "Boa";
      case 'RUIM': return "Ruim";
      case 'VAZIA': return "Vazia";
      case 'CRITICA': return "Crítica";
      default: return classification;
    }
  };

  return (
    <Sidebar>
      <div className="p-6 lg:p-8" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-chivo font-bold text-3xl tracking-tight mb-1">Dashboard</h1>
            <p className="text-zinc-500 text-sm">Visão geral das análises de ocupação</p>
          </div>
          <Link to="/new-analysis">
            <Button 
              data-testid="new-analysis-btn"
              className="h-10 px-5 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white gap-2"
            >
              <PlusCircle size={18} strokeWidth={1.5} />
              Nova Análise
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin rounded-full" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
              <div className="bg-white border border-zinc-200 p-4" data-testid="stat-boa">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#34C759" }} />
                  <div>
                    <p className="text-xs text-zinc-500">Boas</p>
                    <p className="font-chivo font-bold text-2xl">{stats?.by_classification?.BOA || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-zinc-200 p-4" data-testid="stat-ruim">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#FF9500" }} />
                  <div>
                    <p className="text-xs text-zinc-500">Ruins</p>
                    <p className="font-chivo font-bold text-2xl">{stats?.by_classification?.RUIM || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-zinc-200 p-4" data-testid="stat-vazia">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#FF3B30" }} />
                  <div>
                    <p className="text-xs text-zinc-500">Vazias</p>
                    <p className="font-chivo font-bold text-2xl">{stats?.by_classification?.VAZIA || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-zinc-200 p-4" data-testid="stat-critica">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: "#8B0000" }} />
                  <div>
                    <p className="text-xs text-zinc-500">Críticas</p>
                    <p className="font-chivo font-bold text-2xl">{stats?.by_classification?.CRITICA || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              <div className="bg-white border border-zinc-200 p-6" data-testid="stat-total-analyses">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-zinc-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500 mb-1">Total de Análises</p>
                    <p className="font-chivo font-bold text-3xl">{stats?.total_analyses || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-zinc-200 p-6" data-testid="stat-categories">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 text-zinc-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500 mb-1">Categorias</p>
                    <p className="font-chivo font-bold text-3xl">{stats?.total_categories || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-zinc-200 p-6" data-testid="stat-avg-occupancy">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center" 
                    style={{ backgroundColor: `${getOccupancyColor(stats?.average_occupancy || 0)}20` }}>
                    <TrendingUp 
                      className="w-6 h-6" 
                      style={{ color: getOccupancyColor(stats?.average_occupancy || 0) }}
                      strokeWidth={1.5} 
                    />
                  </div>
                  <div>
                    <p className="text-sm text-zinc-500 mb-1">Ocupação Média</p>
                    <p className="font-chivo font-bold text-3xl">{stats?.average_occupancy || 0}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart & Recent */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Chart */}
              <div className="bg-white border border-zinc-200 p-6" data-testid="category-chart">
                <h2 className="font-chivo font-bold text-lg mb-6">Ocupação por Categoria</h2>
                {stats?.by_category?.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.by_category} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          width={100} 
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip 
                          formatter={(value) => [`${value}%`, 'Ocupação']}
                          contentStyle={{ 
                            borderRadius: 0, 
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                        />
                        <Bar dataKey="avg_occupancy" radius={0}>
                          {stats.by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={getOccupancyColor(entry.avg_occupancy)} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-zinc-400">
                    <div className="text-center">
                      <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhuma análise ainda</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Recent Analyses */}
              <div className="bg-white border border-zinc-200 p-6" data-testid="recent-analyses">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-chivo font-bold text-lg">Análises Recentes</h2>
                  <Link 
                    to="/history" 
                    className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
                  >
                    Ver todas <ArrowRight size={14} />
                  </Link>
                </div>
                
                {stats?.recent_analyses?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_analyses.map((analysis) => (
                      <Link
                        key={analysis.id}
                        to={`/analysis/${analysis.id}`}
                        className="block p-4 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm mb-1">{analysis.category_name}</p>
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <Calendar size={12} />
                              {formatDate(analysis.created_at)}
                            </div>
                          </div>
                          <div className="text-right">
                            <p 
                              className="text-lg font-chivo font-bold"
                              style={{ color: getClassificationColor(analysis.classification) }}
                            >
                              {getClassificationLabel(analysis.classification || 'RUIM')}
                            </p>
                            <p className="text-xs text-zinc-500">{analysis.occupancy_percentage}%</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-zinc-400">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Nenhuma análise realizada</p>
                    <Link to="/new-analysis" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
                      Criar primeira análise
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
}
